import { Stack, useTheme } from "@mui/material";
import { useEffect } from "react";
import { type Control, useController, useWatch } from "react-hook-form";
import { OverpaidNotice } from "web_myplann/Goals/Components/AmountSections/OverpaidNotice";
import { PensionBreakdownRow } from "web_myplann/Goals/Components/AmountSections/PensionBreakdownRow";
import { RemainingRow } from "web_myplann/Goals/Components/AmountSections/RemainingRow";
import { RentDrawdownPicker } from "web_myplann/Goals/Components/AmountSections/RentDrawdownPicker";
import { RentTotalRow } from "web_myplann/Goals/Components/AmountSections/RentTotalRow";
import { StatePensionCoversAllNotice } from "web_myplann/Goals/Components/AmountSections/StatePensionCoversAllNotice";
import {
	GOAL_AMOUNT_MAX,
	GOAL_MONTHLY_RENT_MAX,
	GOAL_RENT_YEARS_MAX,
} from "web_myplann/Goals/constants";
import type { GoalFormModel } from "web_myplann/Goals/Forms/useGoalForm";
import { getGoalOverpaidExcess } from "web_myplann/Goals/Helpers/getGoalOverpaidExcess";
import { getRentTotal } from "web_myplann/Goals/Helpers/getRentTotal";
import { useGoalCategoryStrings } from "web_myplann/Goals/Hooks/useGoalCategoryStrings";
import { RentDrawdownMode } from "web_myplann/Goals/Types/RentDrawdownMode";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";

type Props = {
	control: Control<GoalFormModel>;
	category: GoalCategoryTypeEnum;
};

export const RentBasedAmountSection: React.FC<Props> = ({
	control,
	category,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const { remainingLabel } = useGoalCategoryStrings(category);

	const isPension = category === GoalCategoryTypeEnum.Pension;

	const { field: drawdownField } = useController({
		control,
		name: "drawdownMode",
	});
	const { field: totalValueField } = useController({
		control,
		name: "totalValue",
	});
	const drawdownMode = drawdownField.value ?? RentDrawdownMode.ToZero;

	const [
		rawMonthlyRent,
		rawStatePension,
		rawRentYears,
		rawOwnFunds,
		currentCategory,
	] = useWatch({
		control,
		name: ["monthlyRent", "statePension", "rentYears", "ownFunds", "category"],
	});

	const monthlyRent = rawMonthlyRent ?? 0;
	const statePension = rawStatePension ?? 0;
	const rentYears = rawRentYears ?? 0;
	const ownFunds = rawOwnFunds ?? 0;

	const effectiveStatePension = isPension ? statePension : 0;
	const privateMonthly = Math.max(0, monthlyRent - effectiveStatePension);
	const totalValue = getRentTotal({
		monthly: privateMonthly,
		drawdownMode,
		rentYears,
	});

	const totalValueOnChange = totalValueField.onChange;
	const isActiveCategory = currentCategory === category;
	useEffect(() => {
		if (!isActiveCategory) return;
		totalValueOnChange(totalValue > 0 ? totalValue : undefined);
	}, [isActiveCategory, totalValue, totalValueOnChange]);

	const remaining = Math.max(0, totalValue - ownFunds);
	const overpaidExcess = getGoalOverpaidExcess({ totalValue, ownFunds });
	const showOwnFunds = totalValue > 0;
	const currencySuffix = t(Resources.Goals.Amount.currencySign);
	const showBreakdown =
		isPension &&
		monthlyRent > 0 &&
		privateMonthly > 0 &&
		effectiveStatePension > 0;
	const statePensionCoversAll =
		isPension && monthlyRent > 0 && privateMonthly === 0;

	return (
		<Stack bgcolor={theme.palette.background.paper}>
			<RentDrawdownPicker
				value={drawdownMode}
				onChange={drawdownField.onChange}
			/>

			<FlatRowFormInput
				control={control}
				name="monthlyRent"
				label={t(Resources.Goals.Form.Pension.monthlyLabel)}
				placeholder={t(Resources.Goals.Form.Pension.monthlyPlaceholder)}
				type="number"
				max={GOAL_MONTHLY_RENT_MAX}
				suffix={currencySuffix}
			/>
			{isPension && (
				<FlatRowFormInput
					control={control}
					name="statePension"
					label={t(Resources.Goals.Form.Pension.statePensionLabel)}
					placeholder={t(Resources.Goals.Form.Pension.statePensionPlaceholder)}
					type="number"
					max={GOAL_MONTHLY_RENT_MAX}
					suffix={currencySuffix}
				/>
			)}
			{drawdownMode === RentDrawdownMode.ToZero && (
				<FlatRowFormInput
					control={control}
					name="rentYears"
					label={t(Resources.Goals.Form.Pension.yearsLabel)}
					placeholder={t(Resources.Goals.Form.Pension.yearsPlaceholder)}
					type="number"
					max={GOAL_RENT_YEARS_MAX}
					suffix={t(Resources.Goals.Form.Pension.yearsSuffix)}
				/>
			)}

			{statePensionCoversAll && (
				<StatePensionCoversAllNotice
					monthlyRent={monthlyRent}
					statePension={statePension}
					currencySuffix={currencySuffix}
				/>
			)}

			{showBreakdown && (
				<PensionBreakdownRow
					monthlyRent={monthlyRent}
					statePension={statePension}
					privateMonthly={privateMonthly}
					currencySuffix={currencySuffix}
				/>
			)}

			{totalValue > 0 && (
				<RentTotalRow
					totalValue={totalValue}
					drawdownMode={drawdownMode}
					rentYears={rentYears}
					currencySuffix={currencySuffix}
				/>
			)}

			{showOwnFunds && (
				<>
					<FlatRowFormInput
						control={control}
						name="ownFunds"
						label={t(Resources.Goals.Form.Pension.ownFundsLabel)}
						placeholder={t(Resources.Goals.Form.Pension.ownFundsPlaceholder)}
						type="number"
						max={GOAL_AMOUNT_MAX}
						suffix={currencySuffix}
						isLast
					/>
					{overpaidExcess > 0 && (
						<OverpaidNotice
							excess={overpaidExcess}
							currencySuffix={currencySuffix}
						/>
					)}
					{remaining > 0 && (
						<RemainingRow label={remainingLabel} amount={remaining} />
					)}
				</>
			)}
		</Stack>
	);
};
